import React from 'react';
import clsx from 'classnames';

export interface MninimalisticListProps<T> {
  items: T[];
  getKey: (item: T, index: number) => React.Key;
  renderText: (item: T, index: number) => React.ReactNode;
  onItemClick?: (item: T, index: number) => void;
  isSelected?: (item: T, index: number) => boolean;
  emptyMessage?: string;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * Simple rounded list with selectable rows, optimized for compact display.
 */
export function MninimalisticList<T>({
  items,
  getKey,
  renderText,
  onItemClick,
  isSelected,
  emptyMessage,
  className,
  size = 'sm',
}: MninimalisticListProps<T>) {
  if (!items || items.length === 0) {
    return (
      <div className={clsx('text-neutral-500 text-xs', className)}>
        {emptyMessage ?? 'No items'}
      </div>
    );
  }

  const liPad = size === 'sm' ? 'px-3 py-2 text-xs' : 'px-3 py-3 text-sm';

  return (
    <ul className={clsx('rounded-xl border border-transparent divide-y divide-transparent overflow-hidden overflow-x-auto', className)}>
      {items.map((item, idx) => {
        const selected = isSelected ? !!isSelected(item, idx) : false;
        return (
          <li
            key={getKey(item, idx)}
            className={clsx(
              liPad,
              'cursor-pointer select-none',
              selected ? 'pac-glass-subtle text-neutral-100' : 'text-neutral-300 hover:border-neutral-500'
            )}
            onClick={onItemClick ? () => onItemClick(item, idx) : undefined}
            aria-selected={selected || undefined}
            role={onItemClick ? 'button' : undefined}
          >
            <div className="inline-block whitespace-nowrap">{renderText(item, idx)}</div>
          </li>
        );
      })}
    </ul>
  );
}

export default MninimalisticList;
